import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import ReservePlaceButton from '../../components/marketing/ReservePlaceButton';
import WaitlistForm from '../../components/marketing/WaitlistForm';
import { recordFirstTouch } from '../../services/acquisitionService';
import { readWaitlistRef } from '../../services/waitlistService';
import { WAITLIST_COPY } from '../content/waitlist';
import {
  normaliseInterest,
  WAITLIST_INTEREST_LABELS,
  WAITLIST_SURFACES,
  type WaitlistCaptureSurface,
} from '../../types/waitlist';
import { COMPANY } from '../content/company';
import MarketingLayout from '../layout/MarketingLayout';

/**
 * The waitlist as a page (W-2). It exists as its own route because campaign
 * CTAs need a destination that is a single Firestore field to change, and it
 * records first touch on mount because someone arriving here straight from a
 * podcast read has no other attribution surface.
 *
 * `?from=` names the capture slot that linked here (validated against the
 * allowlist), `?interest=` carries which assessment they were reading about.
 */

function resolveSurface(search: string): WaitlistCaptureSurface {
  const param = new URLSearchParams(search).get('from');
  return WAITLIST_SURFACES.includes(param as WaitlistCaptureSurface)
    ? (param as WaitlistCaptureSurface)
    : 'waitlist_page';
}

export default function WaitlistPage() {
  const location = useLocation();
  const sourceSurface = resolveSurface(location.search);
  const interest = normaliseInterest(new URLSearchParams(location.search).get('interest'));
  // Back from Stripe without paying: the entry exists, the place is not held.
  const cancelled = new URLSearchParams(location.search).get('cancelled') === '1';
  const resumeRef = cancelled ? readWaitlistRef() : null;

  useEffect(() => {
    try {
      recordFirstTouch();
    } catch (err) {
      console.warn('recordFirstTouch failed', err);
    }
  }, []);

  const body =
    interest && interest !== 'undecided'
      ? WAITLIST_COPY.bodyForInterest(WAITLIST_INTEREST_LABELS[interest])
      : WAITLIST_COPY.body;

  return (
    <MarketingLayout activePage="home">
      <Helmet>
        <title>Join the waitlist · Melio</title>
        <meta
          name="description"
          content="Melio is a small preventive practice. We haven't opened to new members yet—leave your name and we'll write to you first."
        />
        <link rel="canonical" href={`${COMPANY.siteUrl}/waitlist`} />
      </Helmet>

      <section className="mk-wl" aria-labelledby="wl-title">
        <div className="mk-container mk-wl__grid">
          <div>
            <p className="t-details t-muted">The list</p>
            <h1 id="wl-title" className="mk-wl__title t-display">
              We take on a small number of members at a time.
            </h1>
            <p className="mk-wl__body t-body">{body}</p>
            <p className="mk-wl__aside t-body">
              Want to know what a membership actually involves first?{' '}
              <Link to="/membership">Read about the practice</Link>.
            </p>
          </div>
          <div className="mk-wl__form">
            {cancelled && (
              <div className="mk-notice t-body" role="status">
                <p style={{ margin: resumeRef ? '0 0 1rem' : 0 }}>{WAITLIST_COPY.cancelled}</p>
                {resumeRef && <ReservePlaceButton entryRef={resumeRef} label={WAITLIST_COPY.resume} />}
              </div>
            )}
            {!resumeRef && <WaitlistForm sourceSurface={sourceSurface} variant="inline" initialInterest={interest} />}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
